import React, { useState } from "react";
import { StarRating } from "@/components/StarRating";
import { ReadMore } from "@/components/ReadMore";
import { BookCard } from "@/components/BookCard";
import { SectionHeading } from "@/components/SectionHeading";
import { ReviewSection } from "@/components/ReviewSection";
import { RelatedDiscussions } from "@/components/RelatedDiscussions";
import { AdSlot } from "@/components/AdSlot";
import { useI18n } from "@/contexts/I18nContext";

export const EntitySkeleton = () => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
    <div className="flex items-center gap-5 shirorekha pb-5">
      <div className="h-24 w-24 shrink-0 rounded-full skel sm:h-32 sm:w-32" />
      <div className="flex-1 space-y-3"><div className="h-9 w-2/3 skel" /><div className="h-5 w-1/3 skel" /></div>
    </div>
    <div className="mt-6 h-40 skel" />
  </div>
);

const BOOKS_SHOWN = 12;

/**
 * Shared layout for an author or a publisher: mark, name and rating; the
 * long text clamped; then their books (first 12, then "show all"), the one ad,
 * reviews and discussions.
 */
export function EntityPage({ mark, title, subtitle, rating, reviewCount, text, textTestId, books, booksTitle,
  targetType, target, titleTestId, adPlacement }) {
  const { t } = useI18n();
  const [allBooks, setAllBooks] = useState(false);
  const shown = allBooks ? books || [] : (books || []).slice(0, BOOKS_SHOWN);
  const rated = (reviewCount || 0) > 0;
  return (
    <article className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="flex items-center gap-5 shirorekha pb-5">
        {mark}
        <div className="min-w-0">
          <h1 className="text-[1.8rem] leading-[1.3] sm:text-[2.6rem] break-words" data-testid={titleTestId}>{title}</h1>
          {subtitle && <p className="mt-1 text-muted-foreground break-words">{subtitle}</p>}
          {rated && (
            <p className="mt-2 flex items-center gap-2">
              <StarRating value={rating} size={16} />
              <span className="font-semibold">{Number(rating).toFixed(1)}</span>
              <a href="#reviews" className="text-muted-foreground underline-offset-4 hover:underline">({reviewCount} {t("reviews")})</a>
            </p>
          )}
        </div>
      </header>

      {text && <ReadMore text={text} lines={8} testid={textTestId} className="mt-6" />}

      <section className="mt-12" aria-label={booksTitle}>
        <SectionHeading title={`${booksTitle} (${books?.length || 0})`} />
        {books?.length ? (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-6 sm:gap-x-6">
            {shown.map((b) => <li key={b.id}><BookCard book={b} /></li>)}
          </ul>
        ) : <p className="text-muted-foreground">{t("no_books_yet")}</p>}
        {books?.length > BOOKS_SHOWN && !allBooks && (
          <button type="button" onClick={() => setAllBooks(true)} data-testid="show-all-books"
            className="mt-6 w-full rounded-[var(--radius)] border border-border py-2.5 hover:border-primary/50 hover:text-primary">
            {t("show_all_books").replace("{n}", books.length)}
          </button>
        )}
      </section>

      {/* After their books, before reviews: never in a phone's first screen. */}
      {text && books?.length > 0 && <AdSlot placement={adPlacement} className="mt-12" />}

      <div id="reviews" className="mt-12 max-w-3xl scroll-mt-24"><ReviewSection targetType={targetType} targetId={target.id} /></div>
      <div className="mt-12 max-w-3xl"><RelatedDiscussions targetType={targetType} target={target} /></div>
    </article>
  );
}
